import React, { useEffect, useState } from 'react';
import { Col, Flex, Form, Row } from 'antd';
import { useTranslation } from 'react-i18next';
import { CustomButton, CustomInput, CustomTitle } from '../../components';
import theme from '@/assets/theme';
import './TransportConnectionModal.css';
import {
  SIM_API_KEY_INPUT,
  SIM_COURIER_PROVIDER_KEY,
  SIM_DEMO_API_KEY,
} from '@/common/utils/simulatorIds';

interface Credentials {
  [key: string]: string;
}

interface TransportConnectionData {
  provider_key?: string | null;
  required_inputs?: string[];
  instructions?: string;
  credentials?: Credentials;
}

interface FormModel {
  credentials: Credentials;
  value: 'new' | 'basic';
}

export type TransportConnectionModalVariant = 'default' | 'other' | 'simple';

interface TransportConnectionModalProps {
  data?: TransportConnectionData;
  onSuccess: (model: FormModel) => void;
  variant?: TransportConnectionModalVariant;
  /** When set (e.g. Other request modal), secondary action closes without submitting. */
  onDismiss?: () => void;
}

/**
 * Modal component for configuring transport connection credentials
 *
 * @param data - Transport connection configuration data
 * @param onSuccess - Callback function called when form is submitted or cancelled
 * @param variant - "other" shows request-style copy for the generic Other courier
 */
const TransportConnectionModal: React.FC<TransportConnectionModalProps> = ({
  data,
  onSuccess,
  variant = 'default',
  onDismiss,
}) => {
  const { t } = useTranslation('settings');
  const { t: tCommon } = useTranslation('common');
  const [form] = Form.useForm();
  const [isFormValid] = useState<boolean>(false);
  const [courierModal, setCourierModal] = useState<string[]>(['Client ID']);
  const isOtherVariant = variant === 'other';
  const isSimpleVariant = variant === 'simple';

  useEffect(() => {
    if (data?.required_inputs && Array.isArray(data?.required_inputs)) {
      setCourierModal(data?.required_inputs);
    }
  }, [data]);

  useEffect(() => {
    if (data) {
      if (data.credentials && typeof data.credentials === 'object') {
        // EDIT MODE
        const keys = Object.keys(data.credentials);
        setCourierModal(keys);
        form.setFieldsValue(data.credentials); // pre-fill
      } else if (Array.isArray(data.required_inputs)) {
        // ADD MODE
        setCourierModal(data.required_inputs);
        if (data.provider_key === SIM_COURIER_PROVIDER_KEY) {
          // ZoomLogi Simulator: the demo key is public; Verify still calls the simulator.
          form.setFieldsValue({ [SIM_API_KEY_INPUT]: SIM_DEMO_API_KEY });
        }
      }
    }
  }, [data, form]);

  const sendFormData = (model: FormModel): void => {
    onSuccess(model);
  };

  const onFinishForm = (values: Credentials): void => {
    if (values && Object.keys(values).length > 0) {
      const model: FormModel = {
        credentials: { ...values },
        value: 'new',
      };
      sendFormData(model);
    }
  };
  const handleCancel = (): void => {
    const obj = courierModal.reduce((acc: Credentials, key: string) => {
      acc[key] = '';
      return acc;
    }, {});

    const model: FormModel = {
      credentials: obj,
      value: 'basic',
    };
    sendFormData(model);
  };

  const handleSecondaryAction = (): void => {
    if (isOtherVariant && onDismiss) {
      onDismiss();
      return;
    }
    handleCancel();
  };

  const handleSimpleConfirm = (): void => {
    onSuccess({ credentials: {}, value: 'basic' });
  };

  if (isSimpleVariant) {
    return (
      <Flex gap='16px' justify='end' align='center' style={{ marginTop: 8 }}>
        <CustomButton
          text={t('cancel')}
          bgColor='transparent'
          color='black'
          fontSize='14px'
          fontWeight='600'
          boxShadow='none'
          height='auto'
          border='none'
          padding='8px 12px'
          onClick={onDismiss}
        />
        <CustomButton
          text={tCommon('confirm')}
          bgColor='black'
          color='white'
          height='40px'
          width='auto'
          minWidth='120px'
          fontSize='14px'
          fontWeight='600'
          boxShadow='none'
          borderRadius='8px'
          padding='0 24px'
          onClick={handleSimpleConfirm}
        />
      </Flex>
    );
  }

  const fieldLabel = isOtherVariant
    ? theme.themeType === 2
      ? t('otherTransportProviderFieldLabelCarrier')
      : t('otherTransportProviderFieldLabel')
    : null;

  return (
    <>
      {!isOtherVariant ? (
        <>
          <CustomTitle
            text='Instructions'
            fontSize='14px'
            fontWeight='600'
            color='black'
          />
          <p
            className='transport-connection-instructions'
            dangerouslySetInnerHTML={{ __html: data?.instructions ?? '' }}
          ></p>
        </>
      ) : null}

      <Form
        form={form}
        name='validateOnly'
        layout='vertical'
        autoComplete='off'
        onFinish={onFinishForm}
        style={isOtherVariant ? { marginTop: 0 } : undefined}
      >
        <Row gutter={isOtherVariant ? [0, 0] : 12}>
          <Col span={isOtherVariant ? 24 : 12}>
            {courierModal.map((element) => (
              <Form.Item
                key={element}
                className='custom-input form-input-style'
                label={isOtherVariant ? fieldLabel : element}
                name={element}
                required={isOtherVariant}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                style={{
                  fontSize: '14px',
                  fontWeight: 600,
                  marginTop: isOtherVariant ? 0 : '10px',
                  marginBottom: isOtherVariant ? 8 : undefined,
                }}
                rules={[
                  {
                    required: true,
                    message: isOtherVariant
                      ? t('otherTransportProviderFieldRequired')
                      : `Please input ${element}`,
                  },
                ]}
              >
                <CustomInput
                  placeholder={
                    isOtherVariant
                      ? t('otherTransportProviderFieldPlaceholder')
                      : `Paste ${element} here`
                  }
                  height='36px'
                  width='100%'
                  border='0px'
                  bgColor='var(--light-gray)'
                  textColor='var(--text-secondary)'
                  borderRadius={isOtherVariant ? '12px' : '8px'}
                  placeholderColor='var(--text-secondary)'
                  lineHeight='38px'
                />
              </Form.Item>
            ))}
          </Col>
        </Row>

        <Flex
          gap='16px'
          justify='end'
          align='center'
          wrap='wrap'
          style={{ marginTop: isOtherVariant ? 40 : 32 }}
        >
          <CustomButton
            text={isOtherVariant ? t('cancel') : "I'm okay with basic tracking"}
            bgColor={isOtherVariant ? 'transparent' : '#F3F3F3'}
            color='black'
            fontSize='14px'
            fontWeight={isOtherVariant ? '600' : '600'}
            boxShadow='none'
            height={isOtherVariant ? 'auto' : '40px'}
            border='none'
            padding={isOtherVariant ? '8px 12px' : undefined}
            onClick={handleSecondaryAction}
          />
          <CustomButton
            text={
              isOtherVariant ? t('otherTransportProviderSubmit') : 'Confirm'
            }
            bgColor='black'
            color='white'
            height='40px'
            minWidth={isOtherVariant ? '120px' : undefined}
            width={isOtherVariant ? 'auto' : '80px'}
            fontSize='14px'
            fontWeight='600'
            boxShadow='none'
            borderRadius='8px'
            padding={isOtherVariant ? '0 24px' : undefined}
            isFormValid={isFormValid}
            htmlType='submit'
          />
        </Flex>
      </Form>
    </>
  );
};

export default TransportConnectionModal;
